import React, { useEffect, useState } from 'react';
import Loader from '../../components/Loader';
import { get, post } from '../../services/ApiService';
import toast, { Toaster } from 'react-hot-toast';
import moment from 'moment';
import { useParams } from 'react-router-dom';

const TrackingFailureLogs = () => {
  const { logId } = useParams();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  const fetchLog = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await get(`api/tracking/failure-logs/public/${logId}`);
      if (res.success) {
        setLog(res.log);
      } else {
        setLog(null);
        setNotFound(true);
      }
    } catch (error) {
      console.error(error);
      if (error?.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error('Failed to load tracking failure log');
      }
      setLog(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLog();
  }, [logId]);

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      const res = await post(`api/tracking/failure-logs/public/${logId}/acknowledge`, {});
      if (res.success) {
        toast.success('Failure log acknowledged');
        setLog(res.log);
      } else {
        toast.error(res.error || 'Failed to acknowledge log');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to acknowledge log');
    } finally {
      setAcknowledging(false);
    }
  };

  return (
    <div className="min-vh-100 bg-light py-5">
      <Toaster position="top-center" reverseOrder={false} />
      <div className="container" style={{ maxWidth: 960 }}>
        <div className="text-center mb-4">
          <img src="/assets/images/logo.svg" alt="PillPharm" width={180} />
        </div>

        {loading ? (
          <Loader />
        ) : notFound ? (
          <div className="card shadow-sm">
            <div className="card-body text-center py-5">
              <h4 className="mb-2">Tracking failure log not found</h4>
              <p className="text-muted mb-0">
                This link may be invalid or the log may have been removed.
              </p>
            </div>
          </div>
        ) : (
          <div className="card shadow-sm">
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <h4 className="mb-1">Tracking Failure Alert</h4>
                <small className="text-muted">
                  {log.orders?.length || 0} order(s) failed during inventory stock update
                </small>
              </div>
              <span
                className={`badge ${
                  log.acknowledged ? 'bg-success' : 'bg-warning text-dark'
                }`}
              >
                {log.acknowledged ? 'Acknowledged' : 'Pending'}
              </span>
            </div>

            <div className="card-body border-bottom">
              <div className="row g-3 text-muted small">
                <div className="col-md-4">
                  <strong>Created:</strong>{' '}
                  {log.createdAt
                    ? moment(log.createdAt).format('DD MMM YYYY, HH:mm')
                    : '-'}
                </div>
                <div className="col-md-4">
                  <strong>Email sent:</strong>{' '}
                  {log.emailSentAt
                    ? moment(log.emailSentAt).format('DD MMM YYYY, HH:mm')
                    : '-'}
                </div>
                {log.acknowledged && (
                  <div className="col-md-4">
                    <strong>Acknowledged:</strong>{' '}
                    {log.acknowledgedBy || '-'}
                    {log.acknowledgedAt &&
                      ` on ${moment(log.acknowledgedAt).format('DD MMM YYYY, HH:mm')}`}
                  </div>
                )}
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Error</th>
                    <th>Failed At</th>
                  </tr>
                </thead>
                <tbody>
                  {(log.orders || []).map((order) => (
                    <tr key={order.orderId}>
                      <td>{order.orderId}</td>
                      <td style={{ maxWidth: 420 }}>{order.error}</td>
                      <td>
                        {order.failedAt
                          ? moment(order.failedAt).format('DD MMM YYYY, HH:mm')
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!log.acknowledged && (
              <div className="card-footer text-center">
                <button
                  className="btn btn-primary px-4"
                  onClick={handleAcknowledge}
                  disabled={acknowledging}
                >
                  {acknowledging ? 'Acknowledging...' : 'Acknowledge'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackingFailureLogs;
